import { useState, useCallback, useEffect } from 'react';
import { useDatabase } from '../db';
import type { SupplierDocType } from '@lancar-pos/db-schema';

export type Supplier = SupplierDocType;

export function useSupplierStore() {
  const db = useDatabase();
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setIsLoading(true);
    const subscription = db.suppliers
      .find({
        selector: { _deleted: false },
        sort: [{ name: 'asc' }],
      })
      .$.subscribe((docs: Array<{ toMutableJSON: () => Supplier }>) => {
        setSuppliers(docs.map((doc: { toMutableJSON: () => Supplier }) => doc.toMutableJSON() as Supplier));
        setIsLoading(false);
      });

    return () => subscription.unsubscribe();
  }, [db.suppliers]);

  const addSupplier = useCallback(
    async (supplier: Omit<Supplier, 'id' | 'createdAt' | 'updatedAt' | '_deleted'>) => {
      const now = new Date().toISOString();
      await db.suppliers.insert({
        ...supplier,
        id: crypto.randomUUID(),
        createdAt: now,
        updatedAt: now,
        _deleted: false,
      });
    },
    [db.suppliers]
  );

  const updateSupplier = useCallback(
    async (id: string, updates: Partial<Omit<Supplier, 'id' | 'createdAt'>>) => {
      const doc = await db.suppliers.findOne(id).exec();
      if (doc) {
        await doc.patch({
          ...updates,
          updatedAt: new Date().toISOString(),
        });
      }
    },
    [db.suppliers]
  );

  const deleteSupplier = useCallback(
    async (id: string) => {
      const doc = await db.suppliers.findOne(id).exec();
      if (doc) {
        await doc.patch({
          _deleted: true,
          updatedAt: new Date().toISOString(),
        });
      }
    },
    [db.suppliers]
  );

  const getSupplierById = useCallback(
    (id: string): Supplier | undefined => {
      return suppliers.find((s) => s.id === id);
    },
    [suppliers]
  );

  const getActiveSuppliers = useCallback((): Supplier[] => {
    return suppliers.filter((s) => s.isActive);
  }, [suppliers]);

  const searchSuppliers = useCallback(
    (query: string): Supplier[] => {
      const lowerQuery = query.toLowerCase();
      return suppliers.filter(
        (s) =>
          s.isActive &&
          (s.name.toLowerCase().includes(lowerQuery) ||
            s.code.toLowerCase().includes(lowerQuery) ||
            s.phone.toLowerCase().includes(lowerQuery))
      );
    },
    [suppliers]
  );

  const generateSupplierCode = useCallback(async (): Promise<string> => {
    const allSuppliers = await db.suppliers.find().exec();
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    const prefix = `SUP-${year}${month}${day}`;
    
    const todaySuppliers = allSuppliers.filter(s => s.code.startsWith(prefix));
    const nextNumber = todaySuppliers.length + 1;
    
    return `${prefix}-${String(nextNumber).padStart(3, '0')}`;
  }, [db.suppliers]);

  return {
    suppliers,
    isLoading,
    addSupplier,
    updateSupplier,
    deleteSupplier,
    getSupplierById,
    getActiveSuppliers,
    searchSuppliers,
    generateSupplierCode,
  };
}

export type SupplierStore = ReturnType<typeof useSupplierStore>;
